import { useCallback, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ArrowLeft, Download, FilePlus2, FileText, Plus, RefreshCw, Send } from 'lucide-react'
import { Button, Chip, EmptyState, Input, PageHeaderBand } from '@/components/ui'
import { downloadXlsx } from '@/components/ui/ExportDrawer'
import { StepSection } from '@/shell/forms/FormKit'
import { usePermission } from '@/shell/module-system'
import {
  useApproveReview,
  useCancelReview,
  useCreateLetterFromReview,
  usePatchReview,
  usePatchReviewLine,
  usePriceReview,
  useRefreshReview,
  useRejectReview,
  useRemoveReviewLine,
  useReopenReview,
  useRetryPush,
  useSubmitReview,
} from '../api/reviewQueries'
import type { ReviewLinePatch } from '../api/reviewTypes'
import { AddReviewLineDrawer } from '../components/AddReviewLineDrawer'
import { ApproveReviewModal } from '../components/ApproveReviewModal'
import { PushResultBanner } from '../components/PushResultBanner'
import { RejectReviewModal } from '../components/RejectReviewModal'
import { ReviewLegend } from '../components/ReviewLegend'
import { ReviewLinesGrid } from '../components/ReviewLinesGrid'
import { STATUS_META, canEditLines, reviewExportColumns } from '../utils/review-lines'

function fmtWhen(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const EVENT_LABEL: Record<string, string> = {
  created: 'Created',
  submitted: 'Submitted for approval',
  approved: 'Approved',
  pushed: 'Pushed to Odoo',
  push_failed: 'Push finished with failures',
  rejected: 'Rejected',
  reopened: 'Reopened',
  cancelled: 'Cancelled',
  letter_created: 'Customer letter created',
}

/** Work view for one price review: edit, submit, approve and push, then make the letter. */
export function PriceReviewPage() {
  const { reviewId } = useParams({ strict: false }) as { reviewId?: string }
  const navigate = useNavigate()
  const isAdmin = usePermission('admin')
  const { data, isLoading, isError } = usePriceReview(reviewId ?? null)

  const patchReview = usePatchReview()
  const patchLine = usePatchReviewLine()
  const removeLine = useRemoveReviewLine()
  const refresh = useRefreshReview()
  const submit = useSubmitReview()
  const cancel = useCancelReview()
  const reopen = useReopenReview()
  const reject = useRejectReview()
  const approve = useApproveReview()
  const retry = useRetryPush()
  const makeLetter = useCreateLetterFromReview()

  const [addOpen, setAddOpen] = useState(false)
  const [approveOpen, setApproveOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [resetKey, setResetKey] = useState(0)

  const id = reviewId ?? ''
  const { mutate: patchLineMutate } = patchLine
  const { mutate: removeLineMutate } = removeLine
  const { mutate: patchReviewMutate } = patchReview

  const onPatchLine = useCallback(
    (lineId: number, changes: ReviewLinePatch) =>
      patchLineMutate(
        { reviewId: id, lineId, changes },
        {
          onError: (e) => {
            toast.error((e as Error).message)
            setResetKey((k) => k + 1) // put the rejected input back to the saved value
          },
        },
      ),
    [id, patchLineMutate],
  )
  const onRemove = useCallback(
    (lineId: number) => removeLineMutate({ reviewId: id, lineId }, { onError: (e) => toast.error((e as Error).message) }),
    [id, removeLineMutate],
  )

  if (isLoading) return <div className="p-6 text-sm text-secondary">Loading…</div>
  if (isError || !data) {
    return <EmptyState variant="error" title="Review not found" description="It may have been deleted." />
  }
  const { review, lines } = data
  const meta = STATUS_META[review.status]
  const editable = canEditLines(review.status, isAdmin)
  const draft = review.status === 'draft'
  const canLetter = ['approved', 'pushed', 'push_failed'].includes(review.status)

  const fail = (e: unknown) => toast.error((e as Error).message)
  const slug = review.partner_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  return (
    <div data-density="comfortable" data-surface="form" className="flex h-full flex-col overflow-y-auto bg-canvas">
      <PageHeaderBand title={`Price review — ${review.partner_name}`}>
        <Chip variant={meta.variant}>{meta.label}</Chip>
        <Button variant="secondary" size="sm" onClick={() => void navigate({ to: '/pricelists/reviews' } as never)}>
          <ArrowLeft size={14} /> All reviews
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => downloadXlsx(lines, reviewExportColumns(), { filenameBase: `price-review-${slug}` })}
        >
          <Download size={14} /> Workbook
        </Button>

        {editable && review.status !== 'approved' ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={refresh.isPending}
            onClick={() =>
              refresh.mutate(id, {
                onSuccess: (d) => (d.warnings?.length ? toast.warning(d.warnings.join(' ')) : toast.success('Snapshots refreshed')),
                onError: fail,
              })
            }
          >
            <RefreshCw size={14} /> {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </Button>
        ) : null}
        {draft ? (
          <>
            <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>
              <Plus size={14} /> Add product
            </Button>
            <Button variant="primary" size="sm" disabled={submit.isPending} onClick={() => submit.mutate(id, { onSuccess: () => toast.success('Submitted for approval'), onError: fail })}>
              <Send size={14} /> Submit for approval
            </Button>
          </>
        ) : null}
        {(draft || review.status === 'submitted') ? (
          <Button variant="ghost" size="sm" disabled={cancel.isPending} onClick={() => cancel.mutate(id, { onError: fail })}>
            Cancel review
          </Button>
        ) : null}

        {isAdmin && review.status === 'submitted' ? (
          <>
            <Button variant="primary" size="sm" onClick={() => setApproveOpen(true)}>
              Approve…
            </Button>
            <Button variant="danger" size="sm" onClick={() => setRejectOpen(true)}>
              Reject…
            </Button>
          </>
        ) : null}
        {isAdmin && (review.status === 'submitted' || review.status === 'rejected') ? (
          <Button variant="secondary" size="sm" disabled={reopen.isPending} onClick={() => reopen.mutate({ reviewId: id }, { onError: fail })}>
            Reopen as draft
          </Button>
        ) : null}

        {canLetter ? (
          review.letter_id ? (
            <Button variant="secondary" size="sm" onClick={() => void navigate({ to: '/pricelists/letters/$letterId', params: { letterId: review.letter_id! } } as never)}>
              <FileText size={14} /> Open letter
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              disabled={makeLetter.isPending}
              onClick={() =>
                makeLetter.mutate(id, {
                  onSuccess: (r) => void navigate({ to: '/pricelists/letters/$letterId', params: { letterId: r.letter_id } } as never),
                  onError: fail,
                })
              }
            >
              <FilePlus2 size={14} /> {makeLetter.isPending ? 'Creating…' : 'Create customer letter'}
            </Button>
          )
        ) : null}
      </PageHeaderBand>

      <PushResultBanner review={review} lines={lines} isAdmin={isAdmin} retrying={retry.isPending} onRetry={() => retry.mutate(id, { onError: fail })} />

      {review.status === 'rejected' ? (
        <div className="border-b border-warning bg-warning-bg px-6 py-2 text-sm text-warning-text">
          Rejected{review.rejected_by ? ` by ${review.rejected_by}` : ''}: {review.reject_reason}
        </div>
      ) : null}

      <div className="flex w-full flex-col gap-6 px-6 py-6">
        <StepSection
          id="products"
          step={1}
          title="Products"
          description="Leave New price blank to keep the current price. Set Action to Discontinue to remove a product. Press Enter in a New price to jump to the next one."
        >
          <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-tertiary">Pricelist</span>
              <span className="text-md text-primary">{review.pricelist_name ?? `#${review.pricelist_id}`}</span>
            </div>
            <label className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-tertiary">Effective date</span>
              <EffectiveDateField
                key={`${review.effective_date}`}
                value={review.effective_date ?? ''}
                disabled={!editable}
                onCommit={(v) => patchReviewMutate({ reviewId: id, changes: { effective_date: v || null } }, { onError: fail })}
              />
            </label>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-tertiary">Prices as of</span>
              <span className="text-md text-primary">{review.snapshot_at ? fmtWhen(review.snapshot_at) : '—'}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-tertiary">Submitted</span>
              <span className="text-md text-primary">
                {review.submitted_by ? `${review.submitted_by} · ${fmtWhen(review.submitted_at)}` : 'Not yet'}
              </span>
            </div>
          </div>
          <ReviewLegend lines={lines} />
          <ReviewLinesGrid
            lines={lines}
            symbol={review.currency_symbol}
            editable={editable}
            canRemove={draft}
            onPatch={onPatchLine}
            onRemove={onRemove}
            resetKey={resetKey}
            onInvalidMargin={(m) => toast.error(m)}
          />
        </StepSection>

        {review.stage_history.length > 0 ? (
          <StepSection id="history" step={2} title="History">
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-md text-secondary">
              {[...review.stage_history].reverse().map((h, i) => (
                <li key={`${h.at}-${i}`}>
                  <span className="text-primary">{EVENT_LABEL[h.event] ?? h.event}</span>
                  {h.by ? ` · ${h.by}` : ''} · {fmtWhen(h.at)}
                  {h.note ? ` — ${h.note}` : ''}
                </li>
              ))}
            </ul>
          </StepSection>
        ) : null}
      </div>

      <AddReviewLineDrawer reviewId={id} open={addOpen} onOpenChange={setAddOpen} />
      <ApproveReviewModal
        open={approveOpen}
        onOpenChange={setApproveOpen}
        customer={review.partner_name}
        symbol={review.currency_symbol}
        lines={lines}
        initialDate={review.effective_date}
        pending={approve.isPending}
        error={approve.isError ? (approve.error as Error).message : null}
        onConfirm={(date) =>
          approve.mutate(
            { reviewId: id, effective_date: date },
            {
              onSuccess: (d) => {
                setApproveOpen(false)
                if (d.push?.failed.length) toast.error(`${d.push.failed.length} line(s) did not write to Odoo. See the banner.`)
                else toast.success('Prices pushed to the Odoo pricelist')
              },
            },
          )
        }
      />
      <RejectReviewModal
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        customer={review.partner_name}
        pending={reject.isPending}
        error={reject.isError ? (reject.error as Error).message : null}
        onConfirm={(reason) => reject.mutate({ reviewId: id, reason }, { onSuccess: () => setRejectOpen(false) })}
      />
    </div>
  )
}

/** Date input saved on blur; remounted by the parent when the saved value changes. */
function EffectiveDateField({ value, disabled, onCommit }: { value: string; disabled: boolean; onCommit: (v: string) => void }) {
  const [draft, setDraft] = useState(value)
  return (
    <Input
      type="date"
      aria-label="Effective date"
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => draft !== value && onCommit(draft)}
    />
  )
}
